import React, { useState, useEffect, FormEvent } from 'react';
import { Send, ChevronLeft, ChevronRight, ChevronDown, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useKeenSlider } from 'keen-slider/react';
import { CategoryBanners } from '../components/CategoryBanners';
import { BrandFilter } from '../components/BrandFilter';
import { CustomerReviews } from '../components/CustomerReviews';
import { ProductDisplaySection } from '../components/ProductDisplays';
import emailjs from '@emailjs/browser';
import { POPULAR_PRODUCTS } from '../data/popularProducts';
import { fetchProductsLight, preloadProducts } from '../services/products';

interface ProductVariant {
  id: string;
  name: string;
  description: string[];
  price: number;
  discounted_price?: number;
  stock: number;
  color: string;
  color_code: string;
  images: string[]
  isNew?: boolean;
  new?: boolean;
}

interface Product {
  id: string;
  category: string[];
  brand: string;
  tags: string[];
  variants: ProductVariant[];
  isNew?: boolean;
  new?: boolean;
}

export default function Home() {
  const [popularProducts, setPopularProducts] = useState<Product[]>([]);
  const [bluntProducts, setBluntProducts] = useState<Product[]>([]);
  const [aztekProducts, setAztekProducts] = useState<Product[]>([]);
  const [droneProducts, setDroneProducts] = useState<Product[]>([]);
  const [discountedProducts, setDiscountedProducts] = useState<Product[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  
  const [sliderRef] = useKeenSlider({
    slides: {
      perView: 1,
      spacing: 16,
    },
    breakpoints: {
      "(min-width: 640px)": {
        slides: { perView: 2, spacing: 16 },
      },
      "(min-width: 1024px)": {
        slides: { perView: 5, spacing: 16 },
      },
    },
  });

  // Gallery images - you can add more images here
  const galleryImages = [
    'https://res.cloudinary.com/daka2lob2/image/upload/v1751273983/UGC1.1_epveov.jpg',
    'https://res.cloudinary.com/daka2lob2/image/upload/v1751273897/UGC2.1_da6skn.jpg',
    'https://res.cloudinary.com/daka2lob2/image/upload/v1751273383/UGC3.1_eb0ltw.jpg',
    'https://res.cloudinary.com/daka2lob2/image/upload/v1751273896/UGC4.1_fs8e4j.jpg',
    'https://res.cloudinary.com/daka2lob2/image/upload/v1751273557/UGC5.2_f5s9ry.jpg',
    'https://res.cloudinary.com/daka2lob2/image/upload/v1751273897/UGC6.1_d8hxfz.jpg'
  ];

  useEffect(() => {
    const loadProducts = async () => {
      try {
        // Use lightweight data for homepage - faster loading
        const data = await fetchProductsLight();

        // Map popular products with full data
        const populars = POPULAR_PRODUCTS.map(popularProduct => {
          const fullProduct = data.find((p: Product) => p.id === popularProduct.id);
          if (fullProduct && popularProduct.variants && popularProduct.variants[0].id) {
            // Filter the variants to only include the specified variant ID
            fullProduct.variants = fullProduct.variants.filter(v => 
              popularProduct.variants.some(pv => pv.id === v.id)
            );
          }
          return fullProduct || null;
        }).filter(Boolean);
        
        setPopularProducts(populars);
        
        // Filter other product sections
        const blunt = data.filter((p: Product) => 
          p.brand.toLowerCase() === 'blunt' && 
          p.variants?.length > 0
        ).slice(0, 5);
        
        const aztek = data.filter((p: Product) => 
          p.brand.toLowerCase() === 'aztek' && 
          p.variants?.length > 0
        ).slice(0, 5);
        
        const drone = data.filter((p: Product) => 
          p.brand.toLowerCase().includes('drone') && p.variants?.length > 0
        ).slice(0, 5);
        
        const discounted = data.filter((p: Product) => 
          p.variants?.some(v => v.discounted_price)
        ).slice(0, 5);

        setBluntProducts(blunt);
        setAztekProducts(aztek);
        setDroneProducts(drone);
        setDiscountedProducts(discounted);
      } catch (error) {
        console.error('Error fetching products:', error);
        setPopularProducts([]); 
        setBluntProducts([]);
        setAztekProducts([]);
        setDiscountedProducts([]);
      }
    };

    loadProducts();

    // Initialize EmailJS with the correct public key
    emailjs.init("JveHxSEYo9cyKqJ74");

    // Preload products in background for future page visits
    setTimeout(() => {
      preloadProducts();
    }, 3000);
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: null, message: '' });

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      await emailjs.send(
        "service_p298pqg",
        "template_reki4qc",
        {
          name: formData.get('name'),
          email: formData.get('email'),
          subject: formData.get('subject'),
          message: formData.get('message'),
        }
      );

      setFormStatus({
        type: 'success',
        message: 'Sõnum edukalt saadetud! Võtame teiega peagi ühendust.'
      });
      form.reset();
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: 'Vabandust, midagi läks valesti. Palun proovige hiljem uuesti.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Trikitõukeratas ja tõukeratta varuosad - Tõuks.ee</title>
        <meta name="description" content="Osta kvaliteetseid trikitõukseid ja varuosi otse online. Suur valik brändidelt nagu Blunt, Aztek, Drone. Kiire kohaletoimetamine Eestis." />
        <meta name="keywords" content="trikitõuks, scooter, tallad, lenksud, klambrid, kahvlid, tõuksid" />
        <link rel="canonical" href="https://xn--tuks-0qa.ee/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Tõuks.ee - Lai valik trikitõukse ja varuosi" />
        <meta property="og:description" content="Osta kvaliteetseid trikitõukseid ja varuosi otse online." />
        <meta property="og:url" content="https://xn--tuks-0qa.ee/" />
        <meta property="og:image" content="https://xn--tuks-0qa.ee/Touks-icon4.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://xn--tuks-0qa.ee/" />
        <meta name="twitter:title" content="Tõuks.ee - Lai valik trikitõukse ja varuosi" />
        <meta name="twitter:description" content="Osta kvaliteetseid trikitõukseid ja varuosi otse online. Suur valik brändidelt nagu Blunt, Aztek, Drone." />
        <meta name="twitter:image" content="https://xn--tuks-0qa.ee/Touks-icon4.webp" />
        <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"Organization","name":"Tõuks.ee","url":"https://xn--tuks-0qa.ee","logo":"https://res.cloudinary.com/daka2lob2/image/upload/v1751272742/Touks_logo3_ed9w6g.png","description":"Trikitõukside ja varuosade e-pood","telephone":"+3725256424","email":"ametliktouks@gmail.com","contactPoint":{"@type":"ContactPoint","telephone":"+3725256424","contactType":"Customer Service"}})}</script>
        <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":[{"@type":"Question","name":"Mis on trikitõukeratas ja kellele see sobib?","acceptedAnswer":{"@type":"Answer","text":"Trikitõukeratas on tugevdatud konstruktsiooniga tõukeratas, mis on loodud trikkide ja akrobaatika sooritamiseks. Erinevalt tavalisest tõukeratast on trikitõukerattal lühem ja tugevam raam, väiksemad ratid ning parem manööverdamisvõime. See sobib nii alustajatele kui ka kogenud sõitjatele alates 5–6 aastast — meie valikus on trikitõukseid nii lastele kui täiskasvanutele."}},{"@type":"Question","name":"Milliseid trikitõukside brände te müüte?","acceptedAnswer":{"@type":"Answer","text":"Tõuks.ee müüb maailma juhtivate trikitõukside tootjate tooteid: Blunt Scooters, Aztek, Drone, North Scooters, Lucky Scooters ja teised tippmargid. Kõik meie tooted on 100% originaalid, mis pärinevad ametlikelt edasimüüjatelt."}},{"@type":"Question","name":"Mis vanuses lapsed saavad trikitõukeratast sõita?","acceptedAnswer":{"@type":"Answer","text":"Laste tõukeratas sobib tavaliselt alates 5–6 eluaastast. Väiksematele lastele soovitame kergeid ja kompaktseid mudeleid lühema lenksuga. Noorematele algajatele on olemas spetsiaalsed algaja tõuksid, mille hind on soodsam. Aitame rõõmuga sobiva mudeli valikul!"}},{"@type":"Question","name":"Kas te müüte ka tõukeratta varuosi?","acceptedAnswer":{"@type":"Answer","text":"Jah! Tõuks.ee müüb laia valikut tõukeratta varuosi: tallad (dekid), lenksud, rattad, klambrid, koolid ja muud komponendid. Varuosad sobivad nii vigase osa asendamiseks kui ka tõukse uuendamiseks ja kohandamiseks vastavalt oma stiilile."}},{"@type":"Question","name":"Kuidas valida esimest trikitõukeratast algajale?","acceptedAnswer":{"@type":"Answer","text":"Esimese trikitõukse valimisel soovitame arvestada sõitja vanust ja kasvu (lenks peaks ulatuma ligikaudu puusani), talla laiust (algajale sobib 4.5\"–5\" laius) ning rattasuurust (100–110 mm annab stabiilsuse). Algajale sobib kõige paremini ühe tükina komplekteeritud tõuks (complete scooter). Kirjuta meile ja aitame leida sobiva mudeli!"}},{"@type":"Question","name":"Kui kiiresti tellimus kohale jõuab?","acceptedAnswer":{"@type":"Answer","text":"Laos olevad tooted saadame 1–2 tööpäeva jooksul. Kohaletoimetamine DPD, Omniva või SmartPosti kaudu võtab Eestis aega 1–3 tööpäeva. Tellitavad tooted jõuavad kätte 5–14 tööpäeva jooksul. Saadetise staatust saad jälgida tracking-koodi abil."}},{"@type":"Question","name":"Mis on teie tagastustingimused?","acceptedAnswer":{"@type":"Answer","text":"Sul on 14 kalendripäeva aega kaup tagastada alates kättesaamise päevast. Tagastatav toode peab olema kasutamata, originaalpakendis ja terves seisukorras. Defektse toote puhul katame tagastuskulud meie. Raha tagastame 14 päeva jooksul pärast kauba kättesaamist."}}]})}</script>
        <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Kodu","item":"https://xn--tuks-0qa.ee/"}]})}</script>
        {/* WebSite schema — enables Google Sitelinks Search Box */}
        <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"WebSite","name":"Tõuks.ee","url":"https://xn--tuks-0qa.ee","description":"Trikitõukside ja varuosade e-pood Eestis","potentialAction":{"@type":"SearchAction","target":{"@type":"EntryPoint","urlTemplate":"https://xn--tuks-0qa.ee/toukeratas?search={search_term_string}"},"query-input":"required name=search_term_string"}})}</script>
        {/* LocalBusiness schema — helps with local search visibility */}
        <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"OnlineStore","name":"Tõuks.ee","url":"https://xn--tuks-0qa.ee","logo":"https://res.cloudinary.com/daka2lob2/image/upload/v1751272742/Touks_logo3_ed9w6g.png","image":"https://xn--tuks-0qa.ee/Touks-icon4.webp","description":"Eesti suurim trikitõukside ja varuosade e-pood. Laias valikus Blunt, Aztek, Drone, North, Lucky ja teised tippmargid.","email":"ametliktouks@gmail.com","address":{"@type":"PostalAddress","addressCountry":"EE"},"areaServed":{"@type":"Country","name":"Estonia"},"hasOfferCatalog":{"@type":"OfferCatalog","name":"Trikitõuksid ja varuosad","itemListElement":[{"@type":"OfferCatalog","name":"Tõukerattad","url":"https://xn--tuks-0qa.ee/toukeratas"},{"@type":"OfferCatalog","name":"Tallad","url":"https://xn--tuks-0qa.ee/toukeratas/tald"},{"@type":"OfferCatalog","name":"Lenksud","url":"https://xn--tuks-0qa.ee/toukeratas/lenks"}]},"hasMerchantReturnPolicy":{"@type":"MerchantReturnPolicy","applicableCountry":"EE","returnPolicyCategory":"https://schema.org/MerchantReturnFiniteReturnWindow","merchantReturnDays":14,"returnMethod":"https://schema.org/ReturnByMail","returnFees":"https://schema.org/FreeReturn","refundType":"https://schema.org/FullRefund"},"sameAs":[]})}</script>
      </Helmet>
      <main>
      {/* Hero Section */}
      <section className="relative h-[calc(100vh-3rem)] md:h-[calc(100vh+50px)] overflow-hidden">
        <div className="absolute inset-0 hero-gradient z-10"></div>
        <div 
          className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat hero-bg-zoom"
          style={{
            backgroundImage: 'url(https://res.cloudinary.com/daka2lob2/image/upload/v1751272741/Scooter-bg_zztcfl.jpg)',
          }}
          role="img"
          aria-label="Trikitõuks taustapilt"
        />
        <div className="relative z-20 h-full flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Trikitõukeratas ja tõukeratta varuosad,<br /><span className="text-[#4FC1E9]">lai valik Tõuks.ee poes!</span>
          </h1>
          <p className="text-xl md:text-2xl lg:text-2xl text-white mb-8 max-w-2xl">
            Leia endale sobiv tõuks ilma peavaluta kergelt ja mugavalt
          </p>
          <Link
            to="/toukeratas"
            className="bg-[#4FC1E9] hover:bg-[#3ba8d0] text-white font-bold py-3 md:py-4 px-6 md:px-8 rounded-full transition-all duration-300 transform hover:scale-105"
          >
            UURI TOOTEID
          </Link>

          {/* Free shipping notice under CTA */}
          <p className="mt-4 text-white/90 text-sm md:text-base flex items-center gap-2">
            <Truck className="w-4 h-4 text-[#4FC1E9]" />
            Tasuta kuller transport kõikidele tellimustele üle Eesti!
          </p>

          {/* Features - adjusted mobile positioning */}
          <div className="absolute bottom-4 md:bottom-16 w-full flex flex-wrap justify-around text-white max-w-6xl mx-auto bg-black/40 py-4 md:py-8 rounded-xl backdrop-blur-sm px-4">
            <div className="text-center w-1/2 md:w-auto mb-2 md:mb-0">
              <span className="font-bold mb-1 text-sm md:text-base block">KVALITEET</span>
              <p className="text-xs md:text-sm opacity-80">GARANTEERITUD</p>
            </div>
            <div className="text-center w-1/2 md:w-auto mb-2 md:mb-0">
              <span className="font-bold mb-1 text-sm md:text-base block">EKSPERT</span>
              <p className="text-xs md:text-sm opacity-80">NÕUANDED</p>
            </div>
            <div className="text-center w-1/2 md:w-auto">
              <span className="font-bold mb-1 text-sm md:text-base block">ERILISED</span>
              <p className="text-xs md:text-sm opacity-80">TOOTED</p>
            </div>
            <div className="text-center w-1/2 md:w-auto">
              <span className="font-bold mb-1 text-sm md:text-base block">KINDEL</span>
              <p className="text-xs md:text-sm opacity-80">VALIK</p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      {/* Popular Products */}
      <section className="py-12 md:py-20 px-4 md:px-6">
        <div className="container mx-auto">
          {/* Category Banners */}
          <h2 className="text-3xl font-bold text-center mb-8">Vali oma stiil</h2>
          <CategoryBanners />
          
          <div className="mt-32">
          <ProductDisplaySection title="Populaarsed Tooted" products={popularProducts} />
          <div className="text-center mt-8 md:mt-12">
            <Link
              to="/toukeratas"
              className="inline-flex items-center bg-[#4FC1E9] hover:bg-[#3ba8d0] text-white font-bold py-3 px-8 rounded-full transition-all duration-300"
            >
              Vaata kõiki tõukse
            </Link>
          </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section with integrated gallery */}
      <section className="py-12 px-4 md:px-6">
        <div className="container mx-auto">
          <CustomerReviews galleryImages={galleryImages} />
        </div>
      </section>

      {/* Discounted Products */}
      {discountedProducts.length > 0 && (
        <section className="py-12 md:py-20 px-4 md:px-6">
          <div className="container mx-auto">
            <ProductDisplaySection title="Sooduspakkumised" products={discountedProducts} />
            <div className="text-center mt-8">
              <Link
                to="/toukeratas/soodus-tooted"
                className="inline-flex items-center bg-[#4FC1E9] hover:bg-[#3ba8d0] text-white font-bold py-3 px-8 rounded-full transition-all duration-300"
              >
                Uuri soodustusi
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Blunt Products */}
      <section className="py-12 md:py-20 px-4 md:px-6">
        <div className="container mx-auto">
          <ProductDisplaySection title="Blunt tooted" products={bluntProducts} />
          <div className="text-center mt-8">
            <Link
              to="/toukeratas/brandid/blunt"
              className="inline-flex items-center bg-[#4FC1E9] hover:bg-[#3ba8d0] text-white font-bold py-3 px-8 rounded-full transition-all duration-300"
            >
              Uuri Blunt tooteid
            </Link>
          </div>
        </div>
      </section>

      {/* Aztek Products */}
      <section className="py-12 md:py-20 px-4 md:px-6">
        <div className="container mx-auto">
          <ProductDisplaySection title="Aztek tooted" products={aztekProducts} />
          <div className="text-center mt-8">
            <Link
              to="/toukeratas/brandid/aztek"
              className="inline-flex items-center bg-[#4FC1E9] hover:bg-[#3ba8d0] text-white font-bold py-3 px-8 rounded-full transition-all duration-300"
            >
              Uuri Aztek tooteid
            </Link>
          </div>
        </div>
      </section>
      
      {/* Drone Products */}
      <section className="py-12 md:py-20 px-4 md:px-6">
        <div className="container mx-auto">
          <ProductDisplaySection title="Drone tooted" products={droneProducts} />
          <div className="text-center mt-8">
            <Link
              to="/toukeratas/brandid/drone"
              className="inline-flex items-center bg-[#4FC1E9] hover:bg-[#3ba8d0] text-white font-bold py-3 px-8 rounded-full transition-all duration-300"
            >
              Uuri Drone tooteid
            </Link>
          </div>
        </div>
      </section>

      {/* Brand Filter Section */}
      <section className="py-12 px-4 md:px-6">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Meie brändid</h2>
          <BrandFilter compact />
          <div className="flex justify-center mt-8">
            <Link
              to="/toukeratas/brandid"
              className="bg-[#4FC1E9] hover:bg-[#3ba8d0] text-white font-semibold py-3 px-8 rounded-full transition-colors duration-300 inline-block"
            >
              Tutvu brändidega
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-12 md:py-20 bg-transparent relative px-4 md:px-6" id="contact-section">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto backdrop-blur-sm bg-white/80 p-6 md:p-12 rounded-2xl shadow-lg">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 md:mb-12">Võta meiega ühendust</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Nimi
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4FC1E9] focus:border-transparent transition-all duration-300"
                    placeholder="Sinu nimi"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-post
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4FC1E9] focus:border-transparent transition-all duration-300"
                    placeholder="sinu@email.ee"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Teema
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4FC1E9] focus:border-transparent transition-all duration-300"
                  placeholder="Millega saame aidata?"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Sõnum
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4FC1E9] focus:border-transparent transition-all duration-300"
                  placeholder="Kirjuta oma sõnum siia..."
                ></textarea>
              </div>
              {formStatus.message && (
                <div className={`p-4 rounded-lg ${
                  formStatus.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {formStatus.message}
                </div>
              )}
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center bg-[#4FC1E9] hover:bg-[#3ba8d0] text-white font-bold py-3 px-8 rounded-full transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-5 h-5 mr-2" />
                  {isSubmitting ? 'Saadan...' : 'Saada sõnum'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-20 px-4 md:px-6" id="korduma-kippuvad-kusimused">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">Korduma kippuvad küsimused</h2>
          <p className="text-center text-gray-500 mb-10 md:mb-14">Leidsid vastused kiirelt — kui ei, siis kirjuta meile!</p>
          <div className="space-y-3">
            {([
              {
                q: 'Mis on trikitõukeratas ja kellele see sobib?',
                a: 'Trikitõukeratas on tugevdatud konstruktsiooniga tõukeratas, mis on loodud trikkide ja akrobaatika sooritamiseks. Erinevalt tavalisest tõukeratast on trikitõukerattal lühem ja tugevam raam, väiksemad ratid ning parem manööverdamisvõime. See sobib nii alustajatele kui ka kogenud sõitjatele alates 5–6 aastast — meie valikus on trikitõukseid nii lastele kui täiskasvanutele.',
              },
              {
                q: 'Milliseid trikitõukside brände te müüte?',
                a: 'Tõuks.ee müüb maailma juhtivate trikitõukside tootjate tooteid: Blunt Scooters, Aztek, Drone, North Scooters, Lucky Scooters ja teised tippmargid. Kõik meie tooted on 100% originaalid, mis pärinevad ametlikelt edasimüüjatelt.',
              },
              {
                q: 'Mis vanuses lapsed saavad trikitõukeratast sõita?',
                a: 'Laste tõukeratas sobib tavaliselt alates 5–6 eluaastast. Väiksematele lastele soovitame kergeid ja kompaktseid mudeleid lühema lenksuga. Noorematele algajatele on olemas spetsiaalsed algaja tõuksid, mille hind on soodsam. Aitame rõõmuga sobiva mudeli valikul!',
              },
              {
                q: 'Kas te müüte ka tõukeratta varuosi?',
                a: 'Jah! Tõuks.ee müüb laia valikut tõukeratta varuosi: tallad (dekid), lenksud, rattad, klambrid, kahvlid ja muud komponendid. Varuosad sobivad nii vigase osa asendamiseks kui ka tõukse uuendamiseks ja kohandamiseks vastavalt oma stiilile.',
              },
              {
                q: 'Kuidas valida esimest trikitõukeratast algajale?',
                a: 'Esimese trikitõukse valimisel soovitame arvestada sõitja vanust ja kasvu (lenks peaks ulatuma ligikaudu puusani), talla laiust (algajale sobib 4.5"–5" laius) ning rattasuurust (100–110 mm annab stabiilsuse). Algajale sobib kõige paremini ühe tükina komplekteeritud tõuks (complete scooter). Kirjuta meile ja aitame leida sobiva mudeli!',
              },
              {
                q: 'Kui kiiresti tellimus kohale jõuab?',
                a: 'Laos olevad tooted saadame 1–2 tööpäeva jooksul. Kohaletoimetamine DPD, Omniva või SmartPosti kaudu võtab Eestis aega 1–3 tööpäeva. Tellitavad tooted jõuavad kätte 5–14 tööpäeva jooksul. Saadetise staatust saad jälgida tracking-koodi abil.',
              },
              {
                q: 'Mis on teie tagastustingimused?',
                a: 'Sul on 14 kalendripäeva aega kaup tagastada alates kättesaamise päevast. Tagastatav toode peab olema kasutamata, originaalpakendis ja terves seisukorras. Defektse toote puhul katame tagastuskulud meie. Raha tagastame 14 päeva jooksul pärast kauba kättesaamist.',
              },
            ] as { q: string; a: string }[]).map((item, i) => (
              <div key={i} className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left font-semibold text-gray-800 hover:text-[#4FC1E9] transition-colors duration-200"
                  aria-expanded={openFaq === i}
                >
                  <span>{item.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 flex-shrink-0 ml-4 text-[#4FC1E9] transition-transform duration-300 ${
                      openFaq === i ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === i ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <p className="px-6 pb-5 text-gray-600 leading-relaxed">{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
    </>
  );
}
